// Example parent usage
import { useState, useEffect, useCallback } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import DDChoiceEditor from './components/DDChoiceEditor';
import DDZoneEditor from './components/DDZoneEditor';
import DDScoringEditor from './components/DDScoringEditor';
import {
    Debugger,
    QuestionMetaForm,
    TextEntryModal,
    QuestionTitleField,
    type TextEntryModalPartialProps,
} from '../_shared';
import { metaSchema, areItemsUnique } from '../../models/schema';
import { Constants } from '@/src/utils';
import { defaultMeta } from '../../models/questionDTOs';
import {
    ControlledDynamicTextEditor,
    isEditorContentEmpty,
    DragAndDropRenderer,
    type CDTEFieldType,
    type QtiDragAndDropQuestionDTO,
    type QuestionMetaDTO,
} from '@/src/lib';
import { renderLoaders } from '../../utils';
import { saveQuestion } from '../../services';
import { ErrorMessage, SimpleButton, SimpleSwitch } from '@/src/ui';
const qtm = Constants.QUESTION_TYPE_MASTER_IDS;

const config = {
    minZones: 1,
    maxZones: 25,
    minChoices: 1,
    maxChoices: 25,
};

const schema = yup.object({
    id: yup.number().optional(),
    title: yup
        .string()
        .trim()
        .required('Title is required')
        .max(
            Constants.MAX_CHARS.TITLE,
            `Title cannot exceed ${Constants.MAX_CHARS.TITLE} characters`
        ),
    prompt: yup
        .string()
        .test(
            'not-empty',
            'Prompt cannot be empty.',
            (value) => !isEditorContentEmpty(value || '')
        ),
    prompt_es: yup.string().when('$isSpanishAllowed', {
        is: true,
        then: (schema) =>
            schema
                .test(
                    'not-empty',
                    'Prompt (Spanish) cannot be empty.',
                    (value) => !isEditorContentEmpty(value || '')
                )
                .required('Prompt (Spanish) is required'),
        otherwise: (schema) => schema.optional(),
    }),
    backgroundImageUrl: yup.string().trim().required('Background image is required'),
    backgroundImageUrl_es: yup
        .string()
        .trim()
        .when('$isSpanishAllowed', {
            is: true,
            then: (schema) => schema.required('Background image (Spanish) is required'),
            otherwise: (schema) => schema.optional(),
        }),
    designCanvas: yup.object({
        width: yup.number().min(100).required('Width is required'),
        height: yup.number().min(100).required('Height is required'),
    }),
    designCanvas_es: yup.object({
        width: yup.number().required('Width is required'),
        height: yup.number().required('Height is required'),
    }),
    zones: yup
        .array()
        .of(
            yup.object({
                id: yup.string().trim().required('ID is required'),
                label: yup.string().trim().required('Label is required'),
                geometry: yup
                    .object({
                        type: yup.string().oneOf(['rect', 'circle']).required('Type is required'),
                        x: yup.number().when('type', {
                            is: 'rect',
                            then: (schema) => schema.required('X is required').min(0).max(1),
                            otherwise: (schema) => schema.optional(),
                        }),
                        y: yup.number().when('type', {
                            is: 'rect',
                            then: (schema) => schema.required('Y is required').min(0).max(1),
                            otherwise: (schema) => schema.optional(),
                        }),
                        width: yup.number().when('type', {
                            is: 'rect',
                            then: (schema) => schema.required('Width is required').min(0.01).max(1),
                            otherwise: (schema) => schema.optional(),
                        }),
                        height: yup.number().when('type', {
                            is: 'rect',
                            then: (schema) =>
                                schema.required('Height is required').min(0.01).max(1),
                            otherwise: (schema) => schema.optional(),
                        }),
                        cx: yup.number().when('type', {
                            is: 'circle',
                            then: (schema) => schema.required('Center X is required').min(0).max(1),
                            otherwise: (schema) => schema.optional(),
                        }),
                        cy: yup.number().when('type', {
                            is: 'circle',
                            then: (schema) => schema.required('Center Y is required').min(0).max(1),
                            otherwise: (schema) => schema.optional(),
                        }),
                        r: yup.number().when('type', {
                            is: 'circle',
                            then: (schema) =>
                                schema.required('Radius is required').min(0.01).max(0.5),
                            otherwise: (schema) => schema.optional(),
                        }),
                    })
                    .required('Geometry is required'),
                accepts: yup.array().of(yup.string().trim()).required('Accepts is required'),
                maxItems: yup.number().min(1).required('Max items is required'),
                snap: yup.mixed().oneOf(['center', 'origin', 'none']).optional(),
                zIndex: yup.number().optional(),
            })
        )
        .min(config.minZones, `At least ${config.minZones} zone is required`)
        .max(config.maxZones, `Maximum ${config.maxZones} zones allowed`),
    choices: yup
        .array()
        .of(
            yup.object({
                id: yup.string().trim().required('ID is required'),
                label: yup.string().trim().required('Label is required'),
                value: yup
                    .string()
                    .trim()
                    .required('Value is required')
                    .test(
                        'not-empty',
                        'Choice cannot be empty.',
                        (value) => !isEditorContentEmpty(value || '')
                    ),
                value_es: yup.string().when('$isSpanishAllowed', {
                    is: true,
                    then: (schema) =>
                        schema
                            .test(
                                'not-empty',
                                'Value (Spanish) cannot be empty.',
                                (value) => !isEditorContentEmpty(value || '')
                            )
                            .required('Value (Spanish) is required'),
                    otherwise: (schema) => schema.optional(),
                }),
                maxPlacements: yup
                    .number()
                    .typeError('Must be a number')
                    .integer('Must be an integer')
                    .min(1, 'Must be ≥ 1')
                    .default(1),
            })
        )
        .required()
        .min(config.minChoices, `At least ${config.minChoices} choice is required`)
        .max(config.maxChoices, `Maximum ${config.maxChoices} choices allowed`)
        .test('unique', 'Choices must be unique.', (choiceList) =>
            areItemsUnique(choiceList, 'value')
        ),
    scoringRules: yup
        .array()
        .of(
            yup.object({
                zoneId: yup.string().trim().required('ID is required'),
                choiceIds: yup
                    .array()
                    .of(yup.string().trim())
                    .min(1)
                    .required('Choices are required'),
                matchMode: yup
                    .mixed()
                    .oneOf(['any', 'all', 'none'])
                    .required('Match mode is required'),
                weight: yup.number().min(0).required('Weight is required'),
                orderSensitive: yup.boolean().default(false),
            })
        )
        .required()
        .min(1, `At least one scoring rule is required`),
    meta: metaSchema.required(),
});

const defaultCanvas = { width: 1000, height: 600 };

interface DragAndDropEditorProps {
    initialModel?: QtiDragAndDropQuestionDTO;
}

export default function DragAndDropEditor({ initialModel }: DragAndDropEditorProps) {
    const isEdit = !!initialModel;
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isTitleError, setIsTitleError] = useState(false);
    const [modalProps, setModalProps] = useState<TextEntryModalPartialProps | null>(null);

    const [currentLanguage, setCurrentLanguage] = useState<'en' | 'es'>('en');

    const defaultValues: QtiDragAndDropQuestionDTO = initialModel
        ? initialModel
        : {
              title: '',
              prompt: '',
              prompt_es: '',
              backgroundImageUrl: '',
              backgroundImageUrl_es: '',
              designCanvas: { ...defaultCanvas },
              designCanvas_es: { ...defaultCanvas },
              choices: [],
              zones: [],
              scoringRules: [],
              meta: { ...defaultMeta, questionTypeMasterId: qtm.DRAG_AND_DROP },
          };

    const [formContext, setFormContext] = useState({
        isSpanishAllowed: defaultValues.meta.isSpanishAllowed,
    });
    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isSubmitting },
        setError: setFormError,
        clearErrors,
    } = useForm<QtiDragAndDropQuestionDTO>({
        defaultValues,
        resolver: yupResolver(schema as any),
        mode: 'onBlur',
        context: formContext,
    });

    const question = watch();
    useEffect(() => {
        setFormContext({ isSpanishAllowed: question.meta.isSpanishAllowed });
    }, [question.meta.isSpanishAllowed]);

    const handleLanguageSet = useCallback(
        (language: 'en' | 'es') => {
            setCurrentLanguage(language);
            if (language === 'en') return;

            if (!question.prompt_es) {
                setValue('prompt_es', question.prompt);
            }
            if (!question.backgroundImageUrl_es) {
                setValue('backgroundImageUrl_es', question.backgroundImageUrl);
                setValue('designCanvas_es', question.designCanvas);
            }
            question.choices.forEach((choice, index) => {
                if (!choice.value_es) {
                    setValue(`choices.${index}.value_es`, choice.value);
                }
            });
        },
        [question, setValue]
    );

    const onSubmit = async () => {
        if (success) return;
        if (isTitleError) {
            setError('Please choose a unique title.');
            return;
        }

        clearErrors();
        setLoading(true);
        setError(null);
        try {
            const viewMode = isEdit ? 'edit' : 'create';
            await saveQuestion(question, viewMode);
            setSuccess(true);
        } catch (e: any) {
            setError(e.message || 'Unknown error');
            setFormError('root', { type: 'manual', message: e.message || 'Unknown error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <form
                key={`DragAndDropEditor-${question.meta.isSpanishAllowed}`}
                className="p-4 space-y-4"
                onSubmit={handleSubmit(onSubmit)}
            >
                {/* Handles all Spanish entry */}
                {modalProps && (
                    <TextEntryModal
                        initialData={modalProps.initialData}
                        fieldName={modalProps.fieldName}
                        friendlyName={modalProps.friendlyName}
                        textMode={modalProps.textMode}
                        error={modalProps.error}
                        control={control}
                        handleClose={() => setModalProps(null)}
                    />
                )}
                {renderLoaders(loading, success, error, isEdit)}

                <h2>Multiple Choice Question Title:</h2>
                <QuestionTitleField
                    id={question.id}
                    title={question.title}
                    control={control}
                    setIsTitleError={setIsTitleError}
                    error={errors.title}
                />

                <div className="my-4" />

                <div className="flex flex-row gap-4">
                    <h2>Prompt:</h2>
                    {question.meta.isSpanishAllowed && (
                        <div
                            onClick={() =>
                                setModalProps({
                                    initialData: question.prompt_es,
                                    fieldName: 'prompt_es',
                                    friendlyName: 'Prompt (Spanish)',
                                    textMode: 'rich',
                                    error: errors.prompt_es?.message,
                                })
                            }
                            style={{ cursor: 'pointer' }}
                        >
                            Edit Spanish
                        </div>
                    )}
                    <ErrorMessage error={errors.prompt_es} />
                </div>
                <ControlledDynamicTextEditor
                    name="prompt"
                    control={control}
                    initialValue={question.prompt}
                    setOutput={(val: string, field: CDTEFieldType) => {
                        field.onChange(val);
                        setValue('prompt', val, { shouldValidate: true });
                    }}
                    style={{ minHeight: '150px' }}
                />
                <ErrorMessage error={errors.prompt} />

                <h2>Drag Choices:</h2>
                <DDChoiceEditor
                    question={question}
                    setModalProps={setModalProps}
                    control={control}
                    errors={errors}
                />

                <div className="my-4" />

                {question.meta.isSpanishAllowed && (
                    <div>
                        <span>Zone editor language: </span>
                        <SimpleSwitch
                            name="dd-currentLanguage"
                            checked={currentLanguage === 'es'}
                            onChange={(e) => setCurrentLanguage(e.target.checked ? 'es' : 'en')}
                            label={currentLanguage === 'en' ? 'English' : 'Spanish'}
                        />
                    </div>
                )}

                <h2>Drop Zones:</h2>
                <DDZoneEditor
                    question={question}
                    currentLanguage={currentLanguage}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                />

                <div className="my-4" />

                {question.meta.isHandScored ? (
                    <h2>Scoring rules disabled for hand score questions</h2>
                ) : (
                    <>
                        <h2>Scoring:</h2>
                        <DDScoringEditor
                            control={control}
                            errors={errors}
                            zones={question.zones}
                            choices={question.choices}
                            scoringRules={question.scoringRules}
                        />
                    </>
                )}

                <QuestionMetaForm
                    rhfProps={{
                        control,
                        errors: errors.meta,
                        watch,
                    }}
                    handleLanguageSet={handleLanguageSet}
                    disableList={['shouldShuffleOptions', 'shouldShowKeypad']}
                    metaValues={watch('meta')}
                    setMetaValues={(meta: QuestionMetaDTO) => setValue('meta', meta)}
                />

                <div className="border rounded p-4 bg-white max-w-4xl space-y-4 mt-4">
                    <h4 className="font-semibold mb-1">Interactive Preview</h4>
                    <DragAndDropRenderer
                        question={question}
                        options={{
                            showLanguageToggle: question.meta.isSpanishAllowed,
                            showScoring: true,
                        }}
                    />
                </div>

                <div className="flex items-center space-x-2">
                    <div>
                        <SimpleButton
                            type="submit"
                            color="primary"
                            className="bg-green-600 text-white px-3 py-1 rounded text-xs"
                            disabled={isSubmitting || loading}
                        >
                            {isEdit ? 'Update Question' : 'Create Question'}
                        </SimpleButton>
                    </div>
                </div>
                <ErrorMessage
                    error={
                        Object.keys(errors).length ? 'Please correct the errors to continue' : ''
                    }
                />
                {renderLoaders(loading, success, error, isEdit)}

                <Debugger question={question} errors={errors} />
            </form>
        </>
    );
}
